import type { VolunteerOrganization, VolunteerOrganizationLead } from "@entities";
import { VolunteerOrganization as VolunteerOrganizationEntity } from "@entities";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Box } from "@mui/material";
import {
  volunteerOrgActivityDomainRepo,
  volunteerOrgProjectRepo,
  volunteerOrganizationLeadRepo,
  volunteerOrganizationRepo,
} from "@repositories";
import { useSessionStorage } from "@shared/frontend/hooks";
import {
  type ActiveFilter,
  useCreateHyperTable,
} from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import type { SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { SingleRowAction } from "@shared/frontend/hyper-table/inner-components/hyper-table-expand-column/HyperTableExpandColumn";
import {
  isVolunteerOrgBranch,
  volunteerOrgLeadToParentPrefill,
} from "@shared/helpers/volunteer-organization";
import { Roles } from "@shared/Roles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { remult, repo, type EntityFilter } from "remult";
import { useAlert } from "../../../contexts/AlertProvider";
import useQuestion from "../../../dialog-components/QuestionDialog";
import { TableHeaderTitles } from "../table-header/TableHeader";
import { TableToolbarTabs } from "../table-tabs/TableToolbarTabs";
import { buildVolunteerOrganizationColumns } from "./columns";
import { VOLUNTEER_ORGANIZATION_TABLE_FILTERS } from "./filters";
import { VolunteerOrgActivation, VOLUNTEER_ORG_BRANCH_INCLUDE } from "./VolunteerOrgActivation";
import { VolunteerOrgBranchDialog } from "./VolunteerOrgBranchDialog";
import {
  VolunteerOrgDomainsTab,
  VolunteerOrgProjectsTab,
} from "./VolunteerOrgCatalogs";
import { VolunteerOrganizationLeadsTable } from "./VolunteerOrganizationLeadsTable";
import { VolunteerOrgParentDialog } from "./VolunteerOrgParentDialog";

const DISPLAY_TYPE_KEY = "volunteer-organizations-display-type-v3";

type DisplayType =
  | "activation"
  | "parents"
  | "branches"
  | "projects"
  | "domains"
  | "leads";

const PARENT_DIALOG_INCLUDE = {
  activities: { include: { domain: true } },
  handlingManager: { include: { organization: true } },
  contacts: true,
} as const;

const BRANCH_LIST_INCLUDE = {
  handlingManager: true,
  communicationRecords: true,
  activities: { include: { domain: true } },
} as const;

function filtersFingerprint(filters: ActiveFilter<VolunteerOrganization>[]): string {
  return JSON.stringify(
    filters.map((f) => ({
      id: f.id,
      value: f.value,
      meta: f.meta,
      saveInSessionStorage: f.saveInSessionStorage,
    })),
  );
}

const VolunteerOrganizationsPage = () => {
  const { HyperTable } = useCreateHyperTable(VolunteerOrganizationEntity);
  const queryClient = useQueryClient();
  const canManage = remult.isAllowed(Roles.AdminsAndManager);
  const { setAlertText, setSeverity, setTime } = useAlert();
  const question = useQuestion();
  const [displayType, setDisplayType] = useSessionStorage<DisplayType>(
    DISPLAY_TYPE_KEY,
    "activation",
  );
  const [parentDialog, setParentDialog] = useState<VolunteerOrganization | null | "new">(
    null,
  );
  const [parentDialogTab, setParentDialogTab] = useState(0);
  const [convertingLead, setConvertingLead] =
    useState<VolunteerOrganizationLead | null>(null);
  const [branchDialog, setBranchDialog] = useState<{
    parent: VolunteerOrganization;
    branch: VolunteerOrganization | null;
  } | null>(null);

  const hyperFiltersStorageKey = useMemo(() => {
    return `hyper_table_${repo(VolunteerOrganizationEntity).metadata.key}_volunteer-organizations-table_filters`;
  }, []);
  const [storedHyperFilters] = useSessionStorage<any[]>(
    hyperFiltersStorageKey,
    [],
  );
  const [liveHyperFilters, setLiveHyperFilters] = useState<
    ActiveFilter<VolunteerOrganization>[]
  >(storedHyperFilters as ActiveFilter<VolunteerOrganization>[]);

  const handleFiltersChange = useCallback(
    (filters: ActiveFilter<VolunteerOrganization>[]) => {
      setLiveHyperFilters((prev) =>
        filtersFingerprint(prev) === filtersFingerprint(filters)
          ? prev
          : filters,
      );
    },
    [],
  );

  const filterWhere = useMemo((): EntityFilter<VolunteerOrganization> => {
    const values = (liveHyperFilters ?? [])
      .map((f: { value?: unknown }) => f?.value)
      .filter(
        (v): v is Record<string, unknown> =>
          v != null &&
          typeof v === "object" &&
          Object.keys(v as object).length > 0,
      );
    return values.length > 0
      ? ({ $and: values } as EntityFilter<VolunteerOrganization>)
      : {};
  }, [liveHyperFilters]);

  const { data: domains = [] } = useQuery({
    queryKey: ["volunteer-org-activity-domains"],
    queryFn: () =>
      volunteerOrgActivityDomainRepo.find({ orderBy: { sortOrder: "asc" } }),
  });
  const { data: projects = [] } = useQuery({
    queryKey: ["volunteer-org-projects"],
    queryFn: () => volunteerOrgProjectRepo.find({ orderBy: { name: "asc" } }),
  });
  const { data: organizations = [] } = useQuery({
    queryKey: ["volunteer-organizations"],
    enabled: displayType === "projects",
    queryFn: () =>
      volunteerOrganizationRepo.find({
        orderBy: { name: "asc" },
        limit: 2000,
        include: VOLUNTEER_ORG_BRANCH_INCLUDE,
      }),
  });

  const reload = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["volunteer-organizations"] }),
      queryClient.invalidateQueries({ queryKey: ["volunteer-org-branches"] }),
      queryClient.invalidateQueries({ queryKey: ["volunteer-org-parent-branches"] }),
      queryClient.invalidateQueries({ queryKey: ["volunteer-org-projects"] }),
      queryClient.invalidateQueries({
        queryKey: ["volunteer-org-activity-domains"],
      }),
      queryClient.invalidateQueries({ queryKey: ["volunteer-org-assignments"] }),
      HyperTable.context.current?.refetch("invalidate"),
    ]);
  };

  const openParent = async (org: VolunteerOrganization, tab = 0) => {
    const full = await volunteerOrganizationRepo.findId(org.id, {
      include: PARENT_DIALOG_INCLUDE,
    });
    setParentDialogTab(tab);
    setParentDialog(full ?? org);
  };

  const openBranch = async (
    parent: VolunteerOrganization,
    branch: VolunteerOrganization | null,
  ) => {
    if (!branch) {
      setBranchDialog({ parent, branch: null });
      return;
    }
    const full = await volunteerOrganizationRepo.findId(branch.id, {
      include: VOLUNTEER_ORG_BRANCH_INCLUDE,
    });
    setBranchDialog({ parent, branch: full ?? branch });
  };

  const parentForBranch = async (org: VolunteerOrganization) => {
    if (!isVolunteerOrgBranch(org)) return org;
    if (org.parent) return org.parent;
    if (!org.parentId) return null;
    return (await volunteerOrganizationRepo.findId(org.parentId)) ?? null;
  };

  const openRow = useCallback(async (org: VolunteerOrganization) => {
    if (isVolunteerOrgBranch(org)) {
      const parent = await parentForBranch(org);
      if (parent) void openBranch(parent, org);
      return;
    }
    void openParent(org);
  }, []);

  const deleteOrganizations = useCallback(
    async (
      where: EntityFilter<VolunteerOrganization>,
      successMessage = "הארגונים נמחקו בהצלחה",
    ) => {
      try {
        const rows = await volunteerOrganizationRepo.find({
          where,
          limit: 5000,
        });
        const selectedIds = rows.map((row) => row.id);
        const parents = rows.filter((row) => !isVolunteerOrgBranch(row));
        for (const parent of parents) {
          const leftoverChildren = await volunteerOrganizationRepo.count({
            parentId: parent.id,
            id: { $nin: selectedIds },
          });
          if (leftoverChildren > 0) {
            throw new Error(
              `לא ניתן למחוק את "${parent.name}" כל עוד יש לו שלוחות שלא נבחרו`,
            );
          }
        }
        const branchIds = rows.filter(isVolunteerOrgBranch).map((row) => row.id);
        const parentIds = parents.map((row) => row.id);
        if (branchIds.length > 0) {
          await volunteerOrganizationRepo.deleteMany({
            where: { id: { $in: branchIds } },
          });
        }
        if (parentIds.length > 0) {
          await volunteerOrganizationRepo.deleteMany({
            where: { id: { $in: parentIds } },
          });
        }
        setAlertText(successMessage);
        setSeverity("success");
        setTime(2000);
        await reload();
        return true;
      } catch (err) {
        setAlertText(err instanceof Error ? err.message : "מחיקה נכשלה");
        setSeverity("error");
        setTime(3000);
        return false;
      }
    },
    [setAlertText, setSeverity, setTime],
  );

  const getMenuActions = useCallback<SingleRowActionGetter<VolunteerOrganization>>(
    () => {
      const actions: SingleRowAction<VolunteerOrganization>[] = [];
      if (canManage) {
        actions.push(
          {
            text: "הוסף שלוחה",
            icon: <AccountTreeOutlinedIcon />,
            onClick: async (
              _: unknown,
              row: { original: VolunteerOrganization },
            ) => {
              const org = row.original;
              const parent = await parentForBranch(org);
              if (parent) void openBranch(parent, null);
              return { refetch: false };
            },
          },
          {
            text: "עריכה",
            icon: <EditIcon />,
            onClick: (_: unknown, row: { original: VolunteerOrganization }) => {
              void openRow(row.original);
              return { refetch: false };
            },
          },
          {
            text: "מחיקה",
            icon: <DeleteOutlineIcon />,
            onClick: async (
              _: unknown,
              row: { original: VolunteerOrganization },
            ) => {
              const yes = await question(
                `האם למחוק את "${row.original.name}"?`,
              );
              if (!yes) return { refetch: false };
              const deleted = await deleteOrganizations(
                { id: row.original.id },
                "הארגון נמחק בהצלחה",
              );
              return { refetch: deleted };
            },
          },
        );
      }
      return actions;
    },
    [canManage, deleteOrganizations, openRow, question],
  );

  const columns = useMemo(
    () =>
      buildVolunteerOrganizationColumns(
        displayType === "branches" ? "branches" : "parents",
      ),
    [displayType],
  );

  const buttons = useMemo<ActionButton<VolunteerOrganization>[]>(
    () => [
      {
        icon: <DeleteOutlineIcon />,
        title: "מחיקה",
        onClick: async ({ whereToUpdate }) => {
          const yes = await question("האם למחוק את הארגונים שנבחרו?");
          if (!yes) return { refetch: false };
          const deleted = await deleteOrganizations(whereToUpdate);
          return { refetch: deleted, resetSelection: deleted };
        },
      },
    ],
    [deleteOrganizations, question],
  );

  const isActivation = displayType === "activation";
  const isParents = displayType === "parents";
  const isBranches = displayType === "branches";
  const isProjects = displayType === "projects";
  const isDomains = displayType === "domains";
  const isLeads = displayType === "leads";
  const hideTableBody = isActivation || isProjects || isDomains || isLeads;

  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 1 0",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        py: 1.5,
        px: 1.5,
        minWidth: 0,
        width: "100%",
        height: "100%",
        overflowY: "auto",
        borderRadius: "15px",
        bgcolor: "background.paper",
        boxSizing: "border-box",
      }}
    >
      <HyperTable
        tableId="volunteer-organizations-table"
        onFiltersChange={handleFiltersChange}
        components={{
          ...(canManage && !isLeads
            ? {
                addRowButton: {
                  onClick: () => {
                    setConvertingLead(null);
                    setParentDialogTab(0);
                    setParentDialog("new");
                  },
                  content: "ארגון חדש",
                },
              }
            : {}),
          loading: hideTableBody ? null : undefined,
          paginate: hideTableBody ? null : undefined,
          scrollToTop: hideTableBody ? () => null : undefined,
          noResults: hideTableBody ? null : undefined,
        }}
        findConfig={{
          initialOrderBy: { name: "asc" },
          enabled: !hideTableBody,
          where: isParents
            ? { parentId: null }
            : isBranches
              ? ({
                  parentId: { $ne: null as unknown as string },
                } as EntityFilter<VolunteerOrganization>)
              : {},
          include: {
            parent: { include: { handlingManager: true } },
            handlingManager: true,
            communicationRecords: true,
            activities: { include: { domain: true } },
            ...(isParents ? { branches: { include: BRANCH_LIST_INCLUDE } } : {}),
          },
        }}
        tableSettings={
          isParents
            ? {
                getSubRows: (row) => {
                  if (isVolunteerOrgBranch(row)) return [];
                  return (row.branches ?? []).map((branch) => ({
                    ...branch,
                    parent: {
                      id: row.id,
                      name: row.name,
                      handlingManagerId: row.handlingManagerId,
                      handlingManager: row.handlingManager,
                      activities: row.activities,
                    } as VolunteerOrganization,
                  }));
                },
              }
            : undefined
        }
        onRowClick={
          hideTableBody ? undefined : (_, row) => void openRow(row.original)
        }
        title={<TableHeaderTitles titles={[{ content: "ארגוני מתנדבים" }]} />}
      >
        <HyperTable.Toolbar disableViewOptions={isLeads}>
          {!isLeads &&
            VOLUNTEER_ORGANIZATION_TABLE_FILTERS.map((filter) =>
              isEntityFilter(filter) ? (
                <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
              ) : (
                <HyperTable.CustomFilter key={filter.id} {...filter} />
              ),
            )}
          <TableToolbarTabs
            value={displayType}
            onChange={(value) => setDisplayType(value)}
            options={[
              { content: "הפעלה", value: "activation" },
              { content: "ארגונים", value: "parents" },
              { content: "שלוחות", value: "branches" },
              { content: "פרויקטים", value: "projects" },
              { content: "תחומים", value: "domains" },
              { content: "לידים", value: "leads" },
            ]}
          />
        </HyperTable.Toolbar>
        {isLeads ? (
          <VolunteerOrganizationLeadsTable
            onConvertLead={(lead) => {
              setConvertingLead(lead);
              setParentDialogTab(0);
              setParentDialog("new");
            }}
          />
        ) : isActivation ? (
          <VolunteerOrgActivation
            domains={domains}
            filterWhere={filterWhere}
            onEditBranch={(branch) => {
              void parentForBranch(branch).then((parent) => {
                if (parent) void openBranch(parent, branch);
              });
            }}
            onEditParent={(parent) => void openParent(parent)}
          />
        ) : isProjects ? (
          <VolunteerOrgProjectsTab
            projects={projects}
            organizations={organizations}
            onChange={reload}
            onOpenOrganization={(org) => void openRow(org)}
          />
        ) : isDomains ? (
          <VolunteerOrgDomainsTab domains={domains} onChange={reload} />
        ) : (
          <>
            {isParents && <HyperTable.ExpandColumn />}
            {canManage && <HyperTable.SelectColumn />}
            <HyperTable.MenuColumn actions={getMenuActions} />
            {columns.map((column) => (
              <HyperTable.Column key={column.field} {...column} />
            ))}
            {canManage && <HyperTable.ActionsBar buttons={buttons} />}
          </>
        )}
      </HyperTable>

      <VolunteerOrgParentDialog
        open={parentDialog !== null}
        organization={parentDialog === "new" ? null : parentDialog}
        domains={domains}
        projects={projects}
        createDefaults={
          convertingLead
            ? volunteerOrgLeadToParentPrefill(convertingLead)
            : undefined
        }
        initialTab={parentDialogTab}
        onClose={() => {
          setParentDialog(null);
          setParentDialogTab(0);
          setConvertingLead(null);
        }}
        onSaved={async (org, created) => {
          if (created && convertingLead) {
            await volunteerOrganizationLeadRepo.update(convertingLead.id, {
              volunteerOrganizationId: org.id,
              convertedAt: new Date(),
            });
            setConvertingLead(null);
            setAlertText("הליד הומר לארגון מתנדבים");
            setSeverity("success");
            setTime(2000);
          }
          if (created) {
            setDisplayType(org.parentId ? "branches" : "parents");
            const full = await volunteerOrganizationRepo.findId(org.id, {
              include: PARENT_DIALOG_INCLUDE,
            });
            setParentDialog(full ?? org);
            await reload();
            return;
          }
          await reload();
          setParentDialog(null);
          setParentDialogTab(0);
        }}
        onAddBranch={(parent) => {
          setParentDialog(null);
          setParentDialogTab(0);
          void openBranch(parent, null);
        }}
        onOpenBranch={(parent, branch) => {
          setParentDialog(null);
          setParentDialogTab(0);
          void openBranch(parent, branch);
        }}
      />
      <VolunteerOrgBranchDialog
        open={Boolean(branchDialog)}
        parent={branchDialog?.parent ?? null}
        branch={branchDialog?.branch ?? null}
        domains={domains}
        projects={projects}
        onClose={() => setBranchDialog(null)}
        onSaved={async (saved) => {
          await reload();
          if (!branchDialog) return;
          const full = await volunteerOrganizationRepo.findId(saved.id, {
            include: VOLUNTEER_ORG_BRANCH_INCLUDE,
          });
          setBranchDialog({
            parent: branchDialog.parent,
            branch: full ?? saved,
          });
        }}
      />
    </Box>
  );
};

export default VolunteerOrganizationsPage;
